import { forwardRef } from "react";

import { uniqBy } from "es-toolkit";
import { observer } from "mobx-react-lite";
import Link from "next/link";
import { useTranslations } from "next-intl";

import * as Profile from "@/components/auth/Profile";
import { Dropdown } from "@/components/sidebar/Dropdown";
import { useTooltip } from "@/components/tooltip/Tooltip";
import { useStore } from "@/store";
import { teamPageUrl } from "@/urls";
import { twMerge } from "@/utils/twMerge";

import type { UserState } from "@/models/AwarenessModel";
import type { DocumentModel } from "@/models/DocumentModel";
import type { Ref } from "react";

interface Props {
  doc: DocumentModel;
  className?: string;
  withUnderline?: boolean;
  maxUsersToShow?: number;
}

const UserPreview = forwardRef<
  HTMLAnchorElement | HTMLDivElement,
  {
    user?: UserState;
    renderPlace?: "dropdown" | "list";
    withUnderline?: boolean;
    isCollapsed?: boolean;
  }
>(({ user, renderPlace = "list", withUnderline = true, isCollapsed, ...props }, ref) => {
  const t = useTranslations("doc");
  const name = user?.username ? user.name || "" : t("awareness.anonymous");

  const { tooltip, handlerProps } = useTooltip({ title: name });

  if (!user) {
    return null;
  }

  const wrapperClassName = twMerge(
    "relative flex flex-col items-center",
    !user.username && "cursor-default",
    withUnderline && renderPlace === "list" && (isCollapsed ? "ml-[4px]" : "-mb-[4px]"),
    renderPlace === "list" &&
      twMerge(
        "hover:z-10 hover:opacity-80 active:opacity-60",
        isCollapsed ? "-mb-1.5 last:mb-0" : "-mr-1.5 last:mr-0",
      ),
    renderPlace === "dropdown" && "flex-row w-full rounded-6 p-1.25",
    "data-[focus]:bg-sidebar-button-active-bg",
  );
  const content =
    renderPlace === "list" ? (
      <div
        {...handlerProps}
        className={twMerge("flex gap-0.5 items-center justify-center", !isCollapsed && "flex-col")}
      >
        <Profile.Userpic
          src={user.avatarUrl}
          name={name}
          color={user.twColor}
          className="w-7 h-7 sm:w-7 sm:h-7 text-size-15"
        />

        {withUnderline && (
          <div
            className={twMerge(
              `bg-${user.twColor}-500 rounded-full`,
              isCollapsed ? "h-[18px] w-[2px]" : "w-[18px] h-[2px]",
            )}
          />
        )}
        {tooltip}
      </div>
    ) : (
      <div {...handlerProps} className="flex flex-row items-center">
        <Profile.Userpic name={name} src={user.avatarUrl} color={user.twColor} />
        <div
          className={twMerge("flex flex-1 flex-col w-full ml-1.5", "leading-[1.2] tracking-tight")}
        >
          <div>
            <div
              className={twMerge(
                "flex-1 truncate text-[15px] leading-[18px] font-semibold tracking-tight",
                "sm:text-[17px]",
              )}
            >
              {name}
            </div>
            {user.username && <div className="text-gray-500 text-[14px]">@{user.username}</div>}
          </div>
        </div>
      </div>
    );

  // In the list the name is only a tooltip, and an avatar without a photo shows initials
  const isList = renderPlace === "list";

  if (!user.username) {
    return (
      <div
        {...props}
        role={isList ? "img" : undefined}
        aria-label={isList ? name : undefined}
        className={wrapperClassName}
      >
        {content}
      </div>
    );
  }

  return (
    <Link
      {...props}
      ref={ref as Ref<HTMLAnchorElement>}
      href={teamPageUrl({ teamPermalink: user.username })}
      aria-label={isList ? name : undefined}
      className={wrapperClassName}
    >
      {content}
    </Link>
  );
});

export const AwarenessUsers = observer(function AwarenessUsers({
  doc,
  withUnderline = true,
  maxUsersToShow = 2,
}: Props) {
  const t = useTranslations("doc");
  const { view } = useStore();

  let { userStates: users = [] } = doc.awareness ?? {};
  users = uniqBy(users, (user) => user?.username);

  if (users.length === 0) return null;

  let usersToShow: UserState[] = [];
  let usersToHide: UserState[] = [];

  if (users.length > maxUsersToShow) {
    usersToShow = users.slice(0, maxUsersToShow - 1);
    usersToHide = users.slice(maxUsersToShow - 1);
  } else {
    usersToShow = users;
  }

  const items = usersToHide.map((user) => ({
    title: user?.name || t("awareness.anonymous"),
    component: () => <UserPreview user={user} renderPlace="dropdown" />,
  }));

  return (
    <div
      className={twMerge(
        "flex flex-nowrap items-center justify-end",
        view.isRightSidebarCollapsed && "flex-col",
      )}
    >
      {usersToShow.map((user, index) => {
        return (
          <UserPreview
            key={user.username || `user-${index}`}
            user={user}
            withUnderline={withUnderline}
            isCollapsed={view.isRightSidebarCollapsed}
          />
        );
      })}
      {items.length > 0 && (
        <Dropdown
          anchor={{ to: "top end", padding: "10px" }}
          toggleButtonLabel={t("awareness.showAllUsers")}
          toggleButtonComponent={
            <div className="relative flex items-center justify-center rounded-full w-7 h-7 border bg-sidebar-bg border-gray-400 dark:border-gray-600 text-document-text text-[16px] text-center tracking-tight">
              {items.length}
            </div>
          }
          items={items}
        />
      )}
    </div>
  );
});
